import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import {defer, forkJoin, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {gte as semverGte} from 'semver';
import {AuthService} from '../../components/auth/auth.service';
import {DEFAULT_TAB_DEFINITIONS} from '../../components/tabs/default-tab-definitions';
import {TabDefinition} from '../../components/tabs/tab-definition';
import {WatchlistService} from '../../components/watchlist';
import {AppConfigService} from '../../services/config.service';
import {LocalStorageService} from '../../services/local-storage.service';
import {SettingsApiService} from '../../services/settings-api.service';
import {Dashboard, DashboardDefinition} from './dashboard';
import {DashboardFactoryService} from './dashboard-factory.service';

const SETTINGS_API_DASHBOARD_KEY = 'web-dashboard';

/**
 * This flag in LocalStorage (prepended with {@link AppConfig.localStorageKeyPrefix})
 * tells {@link DashboardStorageService} to use LocalStorage as a storage for dashboard.
 */
const USE_LOCAL_STORAGE_FLAG_KEY = 'dashboard-use-local-storage';

@Injectable()
export class DashboardStorageService {

    protected settingsApiDashboardKey: string = SETTINGS_API_DASHBOARD_KEY;

    protected readonly useLocalStorage: boolean = this.localStorageService.get(USE_LOCAL_STORAGE_FLAG_KEY, false);

    protected readonly defaultTabsDefOrGetter: TabDefinition[] | (() => TabDefinition[]) = DEFAULT_TAB_DEFINITIONS;

    private readonly isGuestDashboardEnabled: boolean = !!this.appConfigService.appConfig.isGuestDashboardEnabled;

    constructor(
        protected watchlistService: WatchlistService,
        protected settingsApiService: SettingsApiService,
        protected dashboardFactoryService: DashboardFactoryService,
        protected localStorageService: LocalStorageService,
        protected authService: AuthService,
        protected appConfigService: AppConfigService
    ) {}

    public load(): Observable<Dashboard> {
        const dashboardDef$ = this.fetchDefinition().pipe(
            switchMap(dashboardDef => {
                if (dashboardDef && !this.validateDashboardDefVersion(dashboardDef)) {
                    console.warn(
                        `Reset dashboard by version.`
                        + ` Stored version: ${dashboardDef.version || '-'},`
                        + ` required min. version: ${this.appConfigService.appConfig.dashboardMinVersion}.`
                    );

                    dashboardDef = this.getDefaultDashboardDefinition();
                }

                return of(dashboardDef);
            })
        );
        const watchlists$ = this.appConfigService.appConfig.doFetchWatchlistsOnInit
            ? this.watchlistService.fetchWatchlistsFromServer()
            : of([]);

        return forkJoin({
            dashboardDef: dashboardDef$,
            watchlists:   watchlists$
        }).pipe(
            map(({dashboardDef}): Dashboard =>
                this.dashboardFactoryService.createDashboardByDefinition(
                    dashboardDef,
                    this.getDefaultDashboardDefinition()
                )
            )
        );
    }

    public save(dashboard: Dashboard): Observable<any> {
        const dashboardDef: DashboardDefinition = this.dashboardFactoryService.getDashboardDefinition(dashboard);

        return this.storeDefinition(dashboardDef);
    }

    public resetDashboard(): Observable<Dashboard> {
        return defer(() => {
            const defaultDashboardDef = this.getDefaultDashboardDefinition();

            return this.storeDefinition(defaultDashboardDef).pipe(
                map(() => this.dashboardFactoryService.createDashboardByDefinition(defaultDashboardDef))
            );
        });
    }

    public fetchDefinition(): Observable<DashboardDefinition> {
        const isGuest = !this.authService.userRole && this.isGuestDashboardEnabled;

        return (this.useLocalStorage || isGuest)
            ? of(this.getDefinitionFromLocalStorage()).pipe(
                switchMap(def => def || isGuest
                    ? of(def)
                    : this.fetchDefinitionFromServer()
                )
            )
            : this.fetchDefinitionFromServer();
    }

    private getDefaultDashboardDefinition(): DashboardDefinition {
        return {
            tabs:    this.resolveDefaultTabDefinitions(),
            version: this.appConfigService.appConfig.dashboardMinVersion
        };
    }

    private resolveDefaultTabDefinitions(): TabDefinition[] {
        return _.isFunction(this.defaultTabsDefOrGetter)
            ? this.defaultTabsDefOrGetter()
            : this.defaultTabsDefOrGetter;
    }

    private fetchDefinitionFromServer(): Observable<DashboardDefinition> {
        return this.settingsApiService.fetch<DashboardDefinition>(this.settingsApiDashboardKey);
    }

    private storeDefinition(dashboardDef: DashboardDefinition): Observable<any> {
        const isGuest = !this.authService.userRole && this.isGuestDashboardEnabled;

        return (this.useLocalStorage || isGuest)
            ? defer(() => {
                this.storeDefinitionInLocalStorage(dashboardDef);

                return of(undefined);
            })
            : this.storeDefinitionOnServer(dashboardDef);
    }

    private storeDefinitionOnServer(dashboardDef: DashboardDefinition): Observable<any> {
        return this.settingsApiService.store(this.settingsApiDashboardKey, dashboardDef);
    }

    private getDefinitionFromLocalStorage(): DashboardDefinition {
        return this.localStorageService.get(this.settingsApiDashboardKey);
    }

    private storeDefinitionInLocalStorage(dashboardDef: DashboardDefinition): void {
        this.localStorageService.set(this.settingsApiDashboardKey, dashboardDef);
    }

    private validateDashboardDefVersion(dashboardDef: DashboardDefinition): boolean {
        return !this.appConfigService.appConfig.dashboardMinVersion
            || (
                !!dashboardDef.version
                && semverGte(dashboardDef.version, this.appConfigService.appConfig.dashboardMinVersion)
            );
    }

}
